import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  TextField,
  Select,
  Spinner,
  Banner,
  DataTable,
  Badge,
  Box,
  Modal,
  FormLayout,
  Pagination,
} from '@shopify/polaris';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

export default function AdminStores() {
  const navigate = useNavigate();
  const { isAuthenticated, adminFetch } = useAdminAuth();
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [selectedStore, setSelectedStore] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [trialDays, setTrialDays] = useState('30');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountDescription, setDiscountDescription] = useState('');
  const [discountExpiresDays, setDiscountExpiresDays] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadStores = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: 20,
      });
      if (search) params.append('search', search);
      if (planFilter) params.append('plan', planFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await adminFetch(`/api/admin/stores?${params}`);
      if (response.ok) {
        const data = await response.json();
        setStores(data.stores);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      } else {
        setError('Failed to load stores');
      }
    } catch (err) {
      setError('Failed to load stores');
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, pagination.page, search, planFilter, statusFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadStores();
  }, [isAuthenticated, navigate, loadStores]);

  const handleUpgrade = async () => {
    if (!selectedStore) return;
    setActionLoading(true);
    try {
      const response = await adminFetch(`/api/admin/stores/${selectedStore.id}/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trial_days: parseInt(trialDays) || 0 }),
      });
      if (response.ok) {
        setShowUpgradeModal(false);
        loadStores();
      } else {
        setError('Failed to upgrade store');
      }
    } catch (err) {
      setError('Failed to upgrade store');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngrade = async (store) => {
    if (!window.confirm(`Downgrade ${store.shop_domain} to basic?`)) return;
    try {
      const response = await adminFetch(`/api/admin/stores/${store.id}/downgrade`, {
        method: 'POST',
      });
      if (response.ok) {
        loadStores();
      } else {
        setError('Failed to downgrade store');
      }
    } catch (err) {
      setError('Failed to downgrade store');
    }
  };

  const handleCreateDiscount = async () => {
    if (!selectedStore || !discountValue) return;
    setActionLoading(true);
    try {
      const response = await adminFetch('/api/admin/discounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: selectedStore.id,
          discount_type: discountType,
          discount_value: parseFloat(discountValue),
          description: discountDescription,
          expires_in_days: discountExpiresDays ? parseInt(discountExpiresDays) : null,
        }),
      });
      if (response.ok) {
        setShowDiscountModal(false);
        setDiscountValue('');
        setDiscountDescription('');
        setDiscountExpiresDays('');
        loadStores();
      } else {
        setError('Failed to create discount');
      }
    } catch (err) {
      setError('Failed to create discount');
    } finally {
      setActionLoading(false);
    }
  };

  const openUpgradeModal = (store) => {
    setSelectedStore(store);
    setTrialDays('30');
    setShowUpgradeModal(true);
  };

  const openDiscountModal = (store) => {
    setSelectedStore(store);
    setShowDiscountModal(true);
  };

  const handleSearch = useCallback(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  if (!isAuthenticated) {
    return null;
  }

  const storeRows = stores.map(store => [
    <Button variant="plain" onClick={() => navigate(`/admin/stores/${store.id}`)}>
      {store.shop_domain}
    </Button>,
    store.shop_name || '-',
    store.shop_email || '-',
    <Badge tone={store.plan_type === 'premium' ? 'success' : 'info'}>
      {store.plan_type}
    </Badge>,
    <Badge tone={store.is_installed ? 'success' : 'critical'}>
      {store.is_installed ? 'Active' : 'Uninstalled'}
    </Badge>,
    store.bars_count || 0,
    store.active_discounts || 0,
    <InlineStack gap="200">
      {store.plan_type !== 'premium' && (
        <Button size="slim" onClick={() => openUpgradeModal(store)}>
          Upgrade
        </Button>
      )}
      {store.plan_type === 'premium' && (
        <Button size="slim" tone="critical" onClick={() => handleDowngrade(store)}>
          Downgrade
        </Button>
      )}
      <Button size="slim" variant="secondary" onClick={() => openDiscountModal(store)}>
        Discount
      </Button>
    </InlineStack>,
  ]);

  return (
    <Page
      title="Stores"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/admin/dashboard') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by domain, name, or email"
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Plan"
                  options={[
                    { label: 'All Plans', value: '' },
                    { label: 'Basic', value: 'basic' },
                    { label: 'Premium', value: 'premium' },
                  ]}
                  value={planFilter}
                  onChange={setPlanFilter}
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Status"
                  options={[
                    { label: 'All Status', value: '' },
                    { label: 'Active', value: 'active' },
                    { label: 'Uninstalled', value: 'uninstalled' },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>
              <Button onClick={handleSearch}>Filter</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Stores Table */}
        <Layout.Section>
          <Card>
            {isLoading ? (
              <Box padding="800">
                <InlineStack align="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            ) : (
              <BlockStack gap="400">
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'text']}
                  headings={['Domain', 'Name', 'Email', 'Plan', 'Status', 'Bars', 'Discounts', 'Actions']}
                  rows={storeRows}
                />
                {pagination.pages > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={pagination.page > 1}
                      hasNext={pagination.page < pagination.pages}
                      onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                  </InlineStack>
                )}
                <Text tone="subdued">
                  Showing {stores.length} of {pagination.total} stores
                </Text>
              </BlockStack>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Upgrade Modal */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={`Upgrade ${selectedStore?.shop_domain}`}
        primaryAction={{
          content: 'Upgrade to Premium',
          onAction: handleUpgrade,
          loading: actionLoading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowUpgradeModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Trial Days"
              type="number"
              value={trialDays}
              onChange={setTrialDays}
              helpText="Set to 0 for immediate premium without trial"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Discount Modal */}
      <Modal
        open={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        title={`Create Discount for ${selectedStore?.shop_domain}`}
        primaryAction={{
          content: 'Create Discount',
          onAction: handleCreateDiscount,
          loading: actionLoading,
          disabled: !discountValue,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowDiscountModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Discount Type"
              options={[
                { label: 'Percentage', value: 'percentage' },
                { label: 'Fixed Amount', value: 'fixed' },
                { label: 'Free Trial Extension', value: 'free_trial' },
              ]}
              value={discountType}
              onChange={setDiscountType}
            />
            <TextField
              label="Discount Value"
              type="number"
              value={discountValue}
              onChange={setDiscountValue}
              helpText={discountType === 'percentage' ? 'Percentage off (e.g., 20 for 20%)' : 'Amount in dollars'}
            />
            <TextField
              label="Description"
              value={discountDescription}
              onChange={setDiscountDescription}
              placeholder="e.g., Early adopter discount"
            />
            <TextField
              label="Expires in Days"
              type="number"
              value={discountExpiresDays}
              onChange={setDiscountExpiresDays}
              helpText="Leave empty for no expiration"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
